import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { marketingApi, IntakeData } from '../api/marketing';

export interface OnboardingState {
  currentStep: number;
  intake: Partial<IntakeData>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  completed: boolean;
  subdomainAvailable: boolean | null;
  subdomainChecking: boolean;
}

const initialState: OnboardingState = {
  currentStep: 1,
  intake: {},
  loading: false,
  saving: false,
  error: null,
  completed: false,
  subdomainAvailable: null,
  subdomainChecking: false,
};

export const loadIntakeAsync = createAsyncThunk(
  'onboarding/loadIntake',
  async () => {
    const response = await marketingApi.getIntake();
    return response;
  }
);

export const saveIntakeAsync = createAsyncThunk(
  'onboarding/saveIntake',
  async (data: Partial<IntakeData>) => {
    const response = await marketingApi.saveIntake(data);
    return response;
  }
);

export const submitIntakeAsync = createAsyncThunk(
  'onboarding/submitIntake',
  async (data: IntakeData) => {
    const response = await marketingApi.submitIntake(data);
    return response;
  }
);

export const checkSubdomainAsync = createAsyncThunk(
  'onboarding/checkSubdomain',
  async (subdomain: string) => {
    const response = await marketingApi.checkSubdomain(subdomain);
    return response;
  }
);

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      if (state.currentStep < 5) {
        state.currentStep += 1;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    updateIntake: (state, action: PayloadAction<Partial<IntakeData>>) => {
      state.intake = { ...state.intake, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSubdomainCheck: (state) => {
      state.subdomainAvailable = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load Intake
      .addCase(loadIntakeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadIntakeAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.intake = action.payload.intake || {};
        state.currentStep = action.payload.currentStep || 1;
        state.completed = action.payload.completed || false;
      })
      .addCase(loadIntakeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load intake data';
      })
      // Save Intake
      .addCase(saveIntakeAsync.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveIntakeAsync.fulfilled, (state, action) => {
        state.saving = false;
        state.intake = { ...state.intake, ...action.payload };
      })
      .addCase(saveIntakeAsync.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to save progress';
      })
      // Submit Intake
      .addCase(submitIntakeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitIntakeAsync.fulfilled, (state) => {
        state.loading = false;
        state.completed = true;
      })
      .addCase(submitIntakeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to submit intake';
      })
      // Check Subdomain
      .addCase(checkSubdomainAsync.pending, (state) => {
        state.subdomainChecking = true;
        state.subdomainAvailable = null;
      })
      .addCase(checkSubdomainAsync.fulfilled, (state, action) => {
        state.subdomainChecking = false;
        state.subdomainAvailable = action.payload.available;
      })
      .addCase(checkSubdomainAsync.rejected, (state) => {
        state.subdomainChecking = false;
        state.subdomainAvailable = null;
      });
  },
});

export const {
  setStep,
  nextStep,
  prevStep,
  updateIntake,
  clearError,
  resetSubdomainCheck,
} = onboardingSlice.actions;

export const selectOnboarding = (state: { onboarding: OnboardingState }) => state.onboarding;
export default onboardingSlice.reducer;
